import { EventEmitter, inject, Injectable, InjectionToken } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { NGXLogger } from "ngx-logger";
import { lastValueFrom } from 'rxjs';
import { ENVIRONMENT, AuthToken, User } from "@company-box/models";

export const AUTH_SVC = new InjectionToken<AuthService>("AUTH_SVC");

@Injectable({
  providedIn: "root",
})
export class AuthService {
  protected httpClient: HttpClient = inject(HttpClient);
  protected environment: any = inject(ENVIRONMENT);
  protected logger: NGXLogger = inject(NGXLogger);


  public update$ = new EventEmitter<any>();

  constructor() {}

  authorize(loginData: {
    username: any;
    password: any;
  }): Promise<AuthToken> {
    return new Promise<AuthToken>((resolve, reject) => {
      this.clearToken();

      const params = new HttpParams()
        .append("grant_type", "password")
        .append("username", loginData.username)
        .append("password", loginData.password);

      lastValueFrom(this.httpClient
        .post(`${this.environment.apiHost}/user/oauth/token`, params, {
          headers: new HttpHeaders({
            "Content-type": "application/x-www-form-urlencoded; charset=utf-8",
          }),
        }))
        .then(
          (res: any) => {
            const token = new AuthToken().deserialize(res);
            this.setToken(token);
            this.update$.emit();
            this.checkToken().then(
              (authenticated) => {
                if (authenticated) {
                  resolve(token!);
                } else {
                  reject({
                    error: "invalid_grant",
                    error_description: "Invalid username or password",
                  });
                }
              },
              (response) => {
                this.logger.debug(response.error);
                reject(response.error);
              }
            );
          },
          (response) => {
            this.logger.debug(response.error);
            reject(response.error);
          }
        );
    });
  }

  getToken(): AuthToken | undefined {
    let token: any = sessionStorage.getItem("AuthToken");
    if (token) token = JSON.parse(token) as AuthToken;

    if (token && token.expiresIn && token.expiresIn > 0) {
      return token;
    } else {
      return undefined;
    }
  }

  setToken(token: AuthToken | undefined): void {
    if (token) sessionStorage.setItem("AuthToken", JSON.stringify(token));
    else this.clearToken();
  }

  checkToken(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.logger.info("Checking user token");
      const token = this.getToken();
      if (!token) {
        resolve(false);
      }
      lastValueFrom(this.httpClient
        .get(`${this.environment.apiHost}/user/oauth/check_token`, {
          params: {
            token: token!.accessToken!,
          },
        }))
        .then(
          (res: any) => {
            this.logger.trace("Check token response", res);
            const authorities: any[] = [];
            (res.authorities as any[]).forEach((role) => {
              authorities.push(role);
            });
            token!.authorities = authorities;
            this.setToken(token);
            resolve(true);
          },
          (error) => {
            this.clearToken();
            this.logger.trace(error.message || error);
            reject(error);
          }
        );
    });
  }

  renewToken(): Promise<AuthToken> {
    return new Promise<AuthToken>((resolve, reject) => {
      this.logger.info("Refreshing user token");
      const token = this.getToken();
      if (!token) {
        reject({ message: "No token to refresh" });
      }
      this.setToken(new AuthToken());
      const headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded");
      const body = "refresh_token=" + token!.refreshToken + "&grant_type=refresh_token";

      lastValueFrom(this.httpClient.post(`${this.environment.apiHost}/user/oauth/token`, body, { headers }))
      .then(
        (res: any) => {
          this.logger.trace("Check token response", res);
          token!.accessToken = res.access_token;
          this.setToken(token);

          this.logger.trace("new token", token);
          resolve(token!);
        },
        (error) => {
          this.clearToken();
          this.logger.trace(error.message || error);
          reject(error);
        }
      );
    });
  }

  clearToken(): void {
    sessionStorage.removeItem("AuthToken");
  }

  logout(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      lastValueFrom(this.httpClient.post(`${this.environment.apiHost}/user/logout`, null))
      .then(
        () => {
          this.logger.info("User logged out");
          this.clearToken();
          resolve(true);
        },
        (error) => {
          this.logger.debug("Unable to log out user", error);
          reject(error);
        }
      );
    });
  }

  validate(authToken: AuthToken | undefined, roles: string[], name: string): boolean {
    if (!authToken && !this.getToken()) {
      return false;
    }

    let passed = true;
    roles.forEach((role) => {
      let inverted = false;
      if (role.startsWith("!")) {
        inverted = true;
        role = role.substr(1);
      }

      const indexOfRole = authToken?.authorities.indexOf("ROLE_" + role) ?? 0;
      if (!inverted && indexOfRole > -1) {
        this.logger.trace("inverted", inverted);
        if (name) {
          this.logger.trace("User has '" + role + "' role, access granted to named resource '" + name + "'");
        } else {
          this.logger.trace("User has '" + role + "' role");
        }
        // this.logger.debug('User has \'' + role + '\' role');
        passed = passed && true;
      } else if (inverted && indexOfRole === -1) {
        this.logger.trace("inverted", inverted);
        if (name) {
          this.logger.trace(
            "User does not have excluded '" + role + "' role, access granted named resource '" + name + "'"
          );
        } else {
          this.logger.trace("User does not have excluded '" + role + "' role");
        }
        passed = passed && true;
      } else {
        this.logger.trace("inverted", inverted);
        if (name) {
          this.logger.trace("User does NOT have access");
        }
        passed = false;
      }
    });

    return passed;
  }

  unblock(userId: number): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      lastValueFrom(this.httpClient.post(`${this.environment.apiHost}/user/user/${userId}/unblock`, null))
      .then(
        (response: any) => {
          this.logger.info("User unblocked");
          resolve(new User().deserialize(response));
        },
        (error) => {
          this.logger.debug("Unable to unblock user", error);
          reject(error);
        }
      );
    });
  }

  lock(userId: number): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      lastValueFrom(this.httpClient.post(`${this.environment.apiHost}/user/user/${userId}/lock`, null))
      .then(
        (response: any) => {
          this.logger.info("User locked");
          resolve(new User().deserialize(response));
        },
        (error) => {
          this.logger.debug("Unable to lock user", error);
          reject(error);
        }
      );
    });
  }

  unlock(userId: number): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      lastValueFrom(this.httpClient.post(`${this.environment.apiHost}/user/user/${userId}/unlock`, null))
      .then(
        (response: any) => {
          this.logger.info("User unlocked");
          resolve(new User().deserialize(response));
        },
        (error) => {
          this.logger.debug("Unable to unlock user", error);
          reject(error);
        }
      );
    });
  }
}
